import { ApiError, DocEditConflict, fetchMarkdown, saveDocEdit } from "./api";
import { state } from "./app-state";
import { getDocById } from "./constants";
import { ui } from "./dom";
// editor.ts ⇄ document-view.ts 互相引用（openDocument / guardLeaveEditor），
// 全部调用都发生在运行期函数体内，ESM live binding 下安全。
import { openDocument } from "./document-view";
import {
  canEditDoc,
  contentPathOf,
  DRAFT_DEBOUNCE_MS,
  draftKey,
  joinBlocks,
  needsLeaveConfirm,
  normalizeContent,
  parseDraft,
  serializeDraft,
  splitBlocks,
  validateChapter,
  WORD_COUNT_DEBOUNCE_MS,
  type EditorDraft,
} from "./editor-core";
import { confirmDialog } from "./modal";
import { hideSelectionAction } from "./selection-ui";
import { hardLogout } from "./session-ui";
import { showToast } from "./toast";
import { countReadableCharacters, formatWordCount } from "./word-count";

let draftTimer = 0;
let wordCountTimer = 0;
let pendingDraft: EditorDraft | null = null;

/* ---------- 分段编辑面（contenteditable）与源稿的互转 ---------- */

function makeBlock(text: string): HTMLParagraphElement {
  const block = document.createElement("p");
  block.className = text.trimStart().startsWith("#")
    ? "editor-block editor-block--heading"
    : "editor-block";
  block.textContent = text;
  return block;
}

export function setEditorContent(source: string): void {
  ui.editorSurface.textContent = "";
  for (const text of splitBlocks(source)) {
    ui.editorSurface.appendChild(makeBlock(text));
  }
}

function textOfNode(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return (node as Text).data;
  if (node instanceof HTMLBRElement) return "\n";
  if (node instanceof HTMLElement) {
    let text = "";
    node.childNodes.forEach((child) => {
      text += textOfNode(child);
    });
    return text;
  }
  return "";
}

/** 只提取文字与换行——浏览器塞进来的任何标记都进不了保存的正文。 */
export function getEditorContent(): string {
  const blocks: string[] = [];
  let loose = "";
  ui.editorSurface.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      loose += (node as Text).data;
      return;
    }
    if (node instanceof HTMLBRElement) {
      loose += "\n";
      return;
    }
    if (node instanceof HTMLElement) {
      if (loose.trim()) blocks.push(loose);
      loose = "";
      blocks.push(textOfNode(node));
    }
  });
  if (loose.trim()) blocks.push(loose);
  return joinBlocks(blocks);
}

/** 回车/粘贴产生的新块补上样式类；标题类跟随首字符实时更新。 */
function normalizeBlockClasses(): void {
  for (const child of Array.from(ui.editorSurface.children)) {
    if (!(child instanceof HTMLElement)) continue;
    child.classList.add("editor-block");
    child.classList.toggle(
      "editor-block--heading",
      (child.textContent || "").trimStart().startsWith("#"),
    );
  }
}

function focusEditorStart(): void {
  ui.editorSurface.focus();
  const firstBlock = ui.editorSurface.firstChild;
  if (firstBlock) {
    const range = document.createRange();
    range.setStart(firstBlock.firstChild ?? firstBlock, 0);
    range.collapse(true);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  }
  window.scrollTo({ top: 0, behavior: "auto" });
}

/* ---------- 入口可见性 ---------- */

export function updateEditAffordances(): void {
  const doc = getDocById(state.currentDocId);
  const can = state.view === "document" && canEditDoc(doc, state.session);
  ui.editDocBtn.hidden = !can || !!state.editor;
  ui.historyBtn.hidden = !can;
}

/* ---------- 草稿备份 ---------- */

function formatClock(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString("zh-CN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function flushDraft(content?: string): void {
  if (!state.editor) return;
  const value = content ?? getEditorContent();
  const key = draftKey(state.editor.docId);
  if (value === state.editor.savedContent) {
    localStorage.removeItem(key);
    ui.editorDraftHint.textContent = "";
    return;
  }
  const now = Date.now();
  localStorage.setItem(
    key,
    serializeDraft({ content: value, baseRelease: state.editor.baseRelease, savedAt: now }),
  );
  ui.editorDraftHint.textContent = `已自动备份 ${formatClock(now)}`;
}

function scheduleDraft(): void {
  window.clearTimeout(draftTimer);
  draftTimer = window.setTimeout(() => flushDraft(), DRAFT_DEBOUNCE_MS);
}

function hideDraftNotice(): void {
  pendingDraft = null;
  ui.editorDraftNotice.hidden = true;
  ui.editorDraftText.textContent = "";
}

function maybeOfferDraft(currentContent: string, release: string): void {
  if (!state.editor) return;
  const key = draftKey(state.editor.docId);
  const draft = parseDraft(localStorage.getItem(key));
  if (!draft) {
    hideDraftNotice();
    return;
  }
  if (draft.content === currentContent) {
    localStorage.removeItem(key);
    hideDraftNotice();
    return;
  }
  pendingDraft = draft;
  const when = new Date(draft.savedAt).toLocaleString("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  let text = `检测到 ${when} 的本地草稿（约 ${countReadableCharacters(draft.content)} 字）`;
  if (draft.baseRelease !== release) text += "；正文在草稿之后已有更新，恢复后请对照检查";
  ui.editorDraftText.textContent = text;
  ui.editorDraftNotice.hidden = false;
}

/* ---------- 字数 ---------- */

function updateEditorWordCount(): void {
  ui.wordCount.textContent = formatWordCount(
    countReadableCharacters(ui.editorSurface.textContent || ""),
  );
  ui.wordCount.hidden = false;
}

function scheduleWordCount(): void {
  window.clearTimeout(wordCountTimer);
  wordCountTimer = window.setTimeout(updateEditorWordCount, WORD_COUNT_DEBOUNCE_MS);
}

/* ---------- 编辑态进入 / 退出 ---------- */

function hideConflict(): void {
  ui.editorConflict.hidden = true;
}

function showConflict(): void {
  ui.editorConflict.hidden = false;
  ui.editorStatus.textContent = "保存冲突，请选择处理方式";
}

function enterEditorDom(): void {
  ui.reader.hidden = true;
  ui.editor.hidden = false;
  ui.docPager.hidden = true;
  ui.tocSection.hidden = true;
  ui.readingProgress.hidden = true;
  ui.viewKicker.textContent = "编辑正文";
  ui.editorNote.value = "";
  setEditorBusy(false);
  hideConflict();
  hideDraftNotice();
}

function exitEditorDom(): void {
  ui.editor.hidden = true;
  ui.reader.hidden = false;
  ui.docPager.hidden = ui.docPager.childElementCount === 0;
  ui.tocSection.hidden = false;
  ui.readingProgress.hidden = false;
  const doc = getDocById(state.currentDocId);
  ui.viewKicker.textContent = doc?.group === "draft" ? "正文阅读" : "文档审阅";
  hideConflict();
  hideDraftNotice();
  ui.editorSurface.textContent = "";
  ui.editorStatus.textContent = "";
  ui.editorDraftHint.textContent = "";
  ui.editorNote.value = "";
}

export async function enterEditMode(): Promise<void> {
  if (state.editor) return;
  const doc = getDocById(state.currentDocId);
  if (!doc || !canEditDoc(doc, state.session)) return;
  ui.editDocBtn.disabled = true;
  ui.readerStatus.textContent = "正在载入最新正文";
  try {
    const source = await fetchMarkdown(doc.file);
    if (!source.release) {
      showToast("当前环境不支持在线编辑（缺少内容版本标头）", "error");
      return;
    }
    const content = normalizeContent(source.text);
    state.contentRelease.set(doc.id, source.release);
    state.editor = {
      docId: doc.id,
      path: contentPathOf(doc),
      baseRelease: source.release,
      savedContent: content,
      status: "editing",
      conflictRelease: null,
    };
    hideSelectionAction();
    enterEditorDom();
    setEditorContent(content);
    maybeOfferDraft(content, source.release);
    updateEditorWordCount();
    ui.editorStatus.textContent = "已进入编辑模式，当前为最新版本";
    ui.readerStatus.textContent = `${doc.title}已进入编辑模式`;
    focusEditorStart();
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      hardLogout();
      return;
    }
    showToast(error instanceof Error ? error.message : "正文加载失败", "error");
  } finally {
    ui.editDocBtn.disabled = false;
    updateEditAffordances();
  }
}

export function exitEditMode(reason: "cancel" | "saved"): void {
  if (!state.editor) return;
  const docId = state.editor.docId;
  window.clearTimeout(draftTimer);
  window.clearTimeout(wordCountTimer);
  state.editor = null;
  exitEditorDom();
  updateEditAffordances();
  if (reason === "saved") {
    state.markdownCache.delete(docId);
    void openDocument(docId);
  } else {
    ui.wordCount.textContent = formatWordCount(
      countReadableCharacters(ui.reader.textContent || ""),
    );
    ui.wordCount.hidden = false;
    ui.editDocBtn.focus();
  }
}

/** 401 强制登出等场景的无确认中止：只复位状态与 DOM，草稿已由防抖/flush 兜底。 */
export function abortEditor(): void {
  if (!state.editor) return;
  window.clearTimeout(draftTimer);
  window.clearTimeout(wordCountTimer);
  state.editor = null;
  exitEditorDom();
  updateEditAffordances();
}

/**
 * 离开编辑态的统一守卫：clean 直接退出返回 true；dirty 先落草稿再弹确认。
 * 返回 false 表示用户选择留下，调用方必须中止后续导航。
 */
export async function guardLeaveEditor(): Promise<boolean> {
  if (!state.editor) return true;
  const content = getEditorContent();
  if (!needsLeaveConfirm(state.editor, content)) {
    exitEditMode("cancel");
    return true;
  }
  flushDraft(content);
  const confirmed = await confirmDialog({
    kicker: "编辑正文",
    title: "放弃未保存的修改？",
    quote: "修改已备份到本地草稿，下次进入编辑时可以恢复。",
    submitText: "离开",
    danger: true,
  });
  if (!confirmed) return false;
  exitEditMode("cancel");
  return true;
}

/* ---------- 保存 ---------- */

function setEditorBusy(busy: boolean): void {
  ui.editorSaveBtn.disabled = busy;
  ui.editorSaveBtn.classList.toggle("is-loading", busy);
  ui.editorCancelBtn.disabled = busy;
  ui.editorNote.disabled = busy;
  const label = ui.editorSaveBtn.querySelector("span");
  if (label) label.textContent = busy ? "正在保存" : "保存";
}

export async function saveEditor(override?: { content: string; note: string }): Promise<boolean> {
  const editor = state.editor;
  if (!editor || editor.status === "saving") return false;
  const content = override ? normalizeContent(override.content) : getEditorContent();
  const note = override ? override.note : ui.editorNote.value.trim();
  if (!override && content === editor.savedContent && editor.status !== "conflict") {
    showToast("没有需要保存的修改");
    return false;
  }
  const validationError = validateChapter(content);
  if (validationError) {
    ui.editorStatus.textContent = validationError;
    return false;
  }

  editor.status = "saving";
  setEditorBusy(true);
  ui.editorStatus.textContent = "正在保存…";
  try {
    const result = await saveDocEdit({
      path: editor.path,
      content,
      base_release: editor.baseRelease,
      ...(note ? { note } : {}),
    });
    editor.baseRelease = result.release;
    editor.savedContent = content;
    editor.status = "editing";
    editor.conflictRelease = null;
    localStorage.removeItem(draftKey(editor.docId));
    state.contentRelease.set(editor.docId, result.release);
    if (result.historyWarning) {
      showToast("已保存，但历史记录写入失败（内容不受影响）", "error");
    } else if (result.unchanged) {
      showToast("内容与线上一致，无需保存");
    } else {
      showToast(result.revisionId !== null ? `已保存，版本 #${result.revisionId}` : "已保存", "success");
    }
    exitEditMode("saved");
    return true;
  } catch (error) {
    if (error instanceof DocEditConflict) {
      editor.status = "conflict";
      editor.conflictRelease = error.currentRelease;
      flushDraft(content);
      showConflict();
      return false;
    }
    editor.status = "editing";
    if (error instanceof ApiError && error.status === 401) {
      flushDraft(content);
      hardLogout();
      return false;
    }
    if (error instanceof ApiError && error.status === 403) {
      showToast("没有编辑权限（需要管理员会话）", "error");
      return false;
    }
    if (error instanceof ApiError && error.status === 400) {
      ui.editorStatus.textContent = error.message;
      return false;
    }
    showToast(
      error instanceof Error && error.message ? error.message : "保存失败，请检查网络后重试",
      "error",
    );
    return false;
  } finally {
    // 无条件复位按钮：保存成功时 exitEditMode 已把 state.editor 置空，
    // 若以 state.editor 为条件，按钮会永远停在“正在保存”禁用态。
    setEditorBusy(false);
    if (state.editor && state.editor.status === "saving") state.editor.status = "editing";
  }
}

/* ---------- 冲突处理 ---------- */

async function reloadLatestIntoEditor(): Promise<void> {
  const editor = state.editor;
  if (!editor) return;
  const doc = getDocById(editor.docId);
  if (!doc) return;
  try {
    const source = await fetchMarkdown(doc.file);
    if (!source.release) {
      showToast("无法获取内容版本标头", "error");
      return;
    }
    const content = normalizeContent(source.text);
    editor.baseRelease = source.release;
    editor.savedContent = content;
    editor.status = "editing";
    editor.conflictRelease = null;
    state.contentRelease.set(editor.docId, source.release);
    setEditorContent(content);
    hideConflict();
    // 我的文本刚被 flushDraft 存进草稿槽：这里会自动亮出「恢复草稿」，形成找回闭环。
    maybeOfferDraft(content, source.release);
    updateEditorWordCount();
    ui.editorStatus.textContent = "已载入最新版本";
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      hardLogout();
      return;
    }
    showToast(error instanceof Error ? error.message : "载入最新版失败", "error");
  }
}

/* ---------- 事件绑定 ---------- */

export function bindEditorEvents(): void {
  try {
    // 让回车产生 <p> 块（Chrome 默认 div；序列化对标签不敏感，这里只为样式一致）。
    document.execCommand("defaultParagraphSeparator", false, "p");
  } catch {
    /* 某些浏览器不支持该命令，忽略。 */
  }

  ui.editDocBtn.addEventListener("click", () => void enterEditMode());
  ui.editorCancelBtn.addEventListener("click", () => void guardLeaveEditor());
  ui.editorSaveBtn.addEventListener("click", () => void saveEditor());

  ui.editorSurface.addEventListener("input", () => {
    normalizeBlockClasses();
    scheduleDraft();
    scheduleWordCount();
  });
  ui.editorSurface.addEventListener("beforeinput", (event) => {
    if (event.inputType.startsWith("format")) event.preventDefault();
  });
  ui.editorSurface.addEventListener("paste", (event) => {
    event.preventDefault();
    const text = event.clipboardData?.getData("text/plain") ?? "";
    if (!text) return;
    document.execCommand("insertText", false, normalizeContent(text).replace(/\n$/, ""));
  });
  ui.editorSurface.addEventListener("drop", (event) => event.preventDefault());

  ui.editorDraftRestoreBtn.addEventListener("click", () => {
    if (!state.editor || !pendingDraft) return;
    setEditorContent(pendingDraft.content);
    hideDraftNotice();
    normalizeBlockClasses();
    scheduleDraft();
    updateEditorWordCount();
    ui.editorStatus.textContent = "已恢复本地草稿，尚未保存";
  });
  ui.editorDraftDiscardBtn.addEventListener("click", () => {
    if (!state.editor) return;
    localStorage.removeItem(draftKey(state.editor.docId));
    hideDraftNotice();
  });

  ui.conflictCopyBtn.addEventListener("click", async () => {
    try {
      await navigator.clipboard.writeText(getEditorContent());
      showToast("已复制我的全文到剪贴板", "success");
    } catch {
      showToast("复制失败，请手动全选复制", "error");
    }
  });
  ui.conflictReloadBtn.addEventListener("click", async () => {
    if (!state.editor) return;
    const confirmed = await confirmDialog({
      kicker: "编辑正文",
      title: "载入最新版重编？",
      quote: "你当前的文字已备份到本地草稿，载入后可在编辑区上方一键恢复。",
      submitText: "载入最新版",
    });
    if (!confirmed) return;
    flushDraft();
    await reloadLatestIntoEditor();
  });

  window.addEventListener("beforeunload", (event) => {
    if (!state.editor) return;
    const content = getEditorContent();
    if (content === state.editor.savedContent) return;
    flushDraft(content);
    event.preventDefault();
    event.returnValue = "";
  });
  window.addEventListener("pagehide", () => {
    if (state.editor) flushDraft();
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden" && state.editor) flushDraft();
  });
}
